/**
 * HUELLA DE UN ELEMENTO DE LA PANTALLA (Roles y Permisos 100% por interfaz).
 *
 * Es lo que permite «marcar» un botón o una sección en la pestaña de Agregar permisos y que, tiempo después,
 * el vigilante (rbac-vigilante.service.ts) lo vuelva a encontrar en la pantalla real para esconderlo.
 *
 * Lógica pura: no importa Angular ni toca `window`. Solo usa el DOM que le pasan (por eso se prueba con
 * objetos falsos en test/huella-rbac.test.mjs). Dos reglas la mantienen estable:
 *  - El selector NO usa nth-child: así los botones repetidos de las filas de una tabla coinciden todos.
 *  - Lo que depende de datos (nombres, montos) no entra: en un botón se usa su propio texto; en una sección,
 *    el texto de su primer título.
 *
 * Ojo: se escribe con anotaciones simples (`any`, `string`) porque el test carga este .ts sin compilador.
 */

export interface HuellaRbac { selector: string; texto: string; etiqueta: string; indice?: number; }
export interface SugerenciaRbac { tipo: string; nombre: string; }

/** Nombre que la pestaña le pone al iframe: junto con `window.self !== window.top` activa el modo marcador. */
export const NOMBRE_VENTANA_MARCADOR = 'rbac-marcador';
/** Atributo con el que el vigilante esconde (la regla CSS global está en styles.css). */
export const ATRIBUTO_OCULTO = 'data-rbac-oculto';
/** Guarda el `display` en línea que tenía el elemento antes de esconderlo (valor|prioridad), para devolvérselo. */
const ATRIBUTO_DISPLAY_PREVIO = 'data-rbac-display';
/**
 * Atributo que el código de las plantillas pone en lo que YA protege con un permiso (`*ngIf="...hasPermission('x')"`).
 * Valor: uno o varios códigos separados por espacio. Lo escribe el codemod scripts/marcar-permisos-en-plantillas.mjs.
 */
export const ATRIBUTO_PERMISO = 'data-rbac-permiso';

const MAX_TEXTO = 60;
const MAX_CLASES = 2;
const MAX_PASOS_SIN_ANCLA = 6;
const MAX_NODOS_TITULO = 3000;

/** ¿Esta ventana es el iframe del modo marcador? (nunca lo es una pestaña normal) */
/**
 * POST que solo CONSULTAN y que una pantalla necesita para cargarse: el marcador los deja pasar (todo otro POST, PUT,
 * PATCH o DELETE se sigue frenando). Cada uno se revisó en el servidor antes de agregarlo.
 * - /api/person/code: el perfil del cliente se carga con él (sin él, «Usuario no encontrado»). Su única escritura es
 *   apagar un bloqueo de cupo ya vencido, lo mismo que pasa cada vez que alguien abre el perfil.
 */
const CONSULTAS_POST = ['/api/person/code'];

/** ¿Esta petición es un POST de solo consulta de la lista? Compara la ruta sin dominio ni parámetros. */
export const esConsultaPost = (metodo: any, url: any): boolean => {
  if (String(metodo).toUpperCase() !== 'POST') return false;
  const ruta = String(url || '').replace(/^https?:\/\/[^/]+/i, '').split('?')[0].replace(/\/+$/, '');
  return CONSULTAS_POST.indexOf(ruta) >= 0;
};

export const esModoMarcador = (win: any): boolean => {
  try {
    return !!win && win.name === NOMBRE_VENTANA_MARCADOR && win.self !== win.top;
  } catch (e) {
    return false;
  }
};

/** Espacios colapsados y recortado a 60 caracteres. */
export const normalizarTexto = (t: any): string => {
  const s = String(t === null || t === undefined ? '' : t).replace(/\s+/g, ' ').trim();
  return s.length > MAX_TEXTO ? s.slice(0, MAX_TEXTO).trim() : s;
};

const tagDe = (el: any): string => {
  if (!el) return '';
  return String(el.localName || el.tagName || '').toLowerCase();
};

const attr = (el: any, nombre: string): string => {
  if (!el || typeof el.getAttribute !== 'function') return '';
  const v = el.getAttribute(nombre);
  return v === null || v === undefined ? '' : String(v);
};

// ───────────────────────── Qué se puede marcar ─────────────────────────

const ETIQUETAS_MARCABLES = ['button', 'a', 'input', 'select', 'textarea', 'label'];
/** Adornos sin identidad propia (un icono dentro de un botón, un <span> suelto): al tocarlos se marca su contenedor. */
const ADORNOS = ['span', 'i', 'b', 'em', 'small', 'svg', 'path', 'circle', 'rect', 'line', 'polyline', 'polygon', 'g', 'use', 'img', 'font'];

/** Botón, enlace, [role=button], campo o etiqueta de campo: lo que el usuario siente como «una acción». */
export const esMarcable = (el: any): boolean => {
  if (!el) return false;
  if (ETIQUETAS_MARCABLES.indexOf(tagDe(el)) >= 0) return true;
  return attr(el, 'role').toLowerCase() === 'button';
};

/**
 * Lo que se marca al tocar `target`. TODO lo visible se puede marcar (campo, select, tarjeta, tabla, columna, encabezado,
 * fila): si lo tocado está dentro de un control (botón, enlace, campo) se marca ese control; si no, el elemento tocado,
 * salvo que sea un adorno sin texto (icono, svg, span vacío), que sube al primer elemento con identidad propia.
 * «¿Querías marcar toda esa parte?» sigue subiendo de contenedor en contenedor (`subirSeleccion` del marcador).
 */
export const elementoMarcable = (target: any): any => {
  let n = target;
  if (n && n.nodeType === 3) n = n.parentElement; // texto: se parte de su elemento
  const inicio = n;
  for (let i = 0; n && i < 60; i++) {
    if (esMarcable(n)) return n;
    const t = tagDe(n);
    if (t === 'body' || t === 'html') break;
    n = n.parentElement;
  }
  // Sin control a la vista: el elemento tocado; si es un adorno, su primer ancestro que no lo sea.
  let e = inicio;
  // (un <span> con texto propio sí es marcable por sí mismo: solo suben los que no dicen nada)
  for (let i = 0; e && i < 10 && ADORNOS.indexOf(tagDe(e)) >= 0 && !normalizarTexto(e.textContent) && e.parentElement; i++) {
    const t = tagDe(e.parentElement);
    if (t === 'body' || t === 'html') break;
    e = e.parentElement;
  }
  return e || inicio || target;
};

/** 'accion' para un control; 'seccion' para cualquier otro contenedor. */
export const tipoDe = (el: any): string => (esMarcable(el) ? 'accion' : 'seccion');

// ───────────────────────── Texto ─────────────────────────

const TITULOS = ['h1', 'h2', 'h3', 'h4', 'strong', 'legend'];

/** Texto del primer título (h1–h4, strong, legend) dentro de un contenedor, en orden del documento. */
const primerTitulo = (raiz: any): string => {
  if (TITULOS.indexOf(tagDe(raiz)) >= 0) {
    const propio = normalizarTexto(raiz.textContent);
    if (propio) return propio;
  }
  const pila: any[] = [raiz];
  let visitados = 0;
  while (pila.length && visitados < MAX_NODOS_TITULO) {
    const n = pila.pop();
    visitados++;
    if (n !== raiz && TITULOS.indexOf(tagDe(n)) >= 0) {
      const t = normalizarTexto(n.textContent);
      if (t) return t;
    }
    const hijos = n.children;
    if (hijos && hijos.length) {
      for (let i = hijos.length - 1; i >= 0; i--) pila.push(hijos[i]); // al revés: sale primero el primer hijo
    }
  }
  return '';
};

/** Texto de la etiqueta de un campo: label[for=id], label que lo envuelve o label hermano anterior (los de un bloque de Filtros). */
const textoDeEtiquetaAsociada = (el: any): string => {
  const id = attr(el, 'id');
  const doc = el.ownerDocument;
  if (id && doc && typeof doc.querySelector === 'function') {
    try {
      const l = doc.querySelector('label[for="' + id.replace(/"/g, '') + '"]');
      if (l) { const t = normalizarTexto(l.textContent); if (t) return t; }
    } catch (e) { /* id raro: se sigue con las otras pistas */ }
  }
  let p = el.parentElement;
  for (let i = 0; p && i < 3; i++) {
    if (tagDe(p) === 'label') {
      const t = normalizarTexto(p.textContent);
      if (t) return t;
    }
    p = p.parentElement;
  }
  // Etiqueta hermana anterior (<div><label>Estado</label><select/></div>) o, con otro envoltorio, la del padre.
  let nodo = el;
  for (let nivel = 0; nodo && nivel < 2; nivel++) {
    let h = nodo.previousElementSibling;
    for (let i = 0; h && i < 4; i++) {
      if (tagDe(h) === 'label') { const t = normalizarTexto(h.textContent); if (t) return t; }
      h = h.previousElementSibling;
    }
    nodo = nodo.parentElement;
    if (nodo && (tagDe(nodo) === 'body' || nodo.children.length > 4)) break;
  }
  return '';
};

/** Nombres con que se puede reconocer un campo, del más a menos específico. El primero es el que se guarda. */
/** Una <label> suelta justo antes del campo (o de su contenedor), sin `for`: «Usuario» sobre su input. */
const textoDeEtiquetaCercana = (el: any): string => {
  let n = el;
  for (let nivel = 0; n && nivel < 3; nivel++) {
    let h = n.previousElementSibling;
    for (let i = 0; h && i < 2; i++) {
      if (tagDe(h) === 'label') {
        const t = normalizarTexto(h.textContent);
        if (t && t.length <= 40) return t;
      }
      h = h.previousElementSibling;
    }
    n = n.parentElement;
  }
  return '';
};

const textosDeCampo = (el: any): string[] => {
  const lista: string[] = [];
  const poner = (t: any) => { const x = normalizarTexto(t); if (x && lista.indexOf(x) < 0) lista.push(x); };
  // Un placeholder de ejemplo («Ej: Mario») no nombra el campo: va al final.
  const ejemplo = /^(ej\b|ej\.|ej:|por ejemplo|e\.g\.)/i.test(normalizarTexto(attr(el, 'placeholder')));
  poner(attr(el, 'aria-label'));
  poner(textoDeEtiquetaAsociada(el));
  poner(textoDeEtiquetaCercana(el));
  if (!ejemplo) poner(attr(el, 'placeholder'));
  poner(attr(el, 'title'));
  poner(attr(el, 'formcontrolname'));
  poner(attr(el, 'name'));
  if (ejemplo) poner(attr(el, 'placeholder'));
  return lista;
};

const textoDeControl = (el: any): string => {
  const t = tagDe(el);
  if (t === 'input') {
    const tipo = attr(el, 'type').toLowerCase();
    if (tipo === 'submit' || tipo === 'button' || tipo === 'reset') {
      return normalizarTexto(attr(el, 'value') || attr(el, 'aria-label') || attr(el, 'title'));
    }
    return textosDeCampo(el)[0] || '';
  }
  // El textContent de un select/textarea trae sus opciones o su contenido (datos): se usa su nombre.
  if (t === 'select' || t === 'textarea') return textosDeCampo(el)[0] || '';
  const propio = normalizarTexto(el.textContent);
  return propio || normalizarTexto(attr(el, 'aria-label') || attr(el, 'title'));
};

/** Todos los textos con los que una huella guardada puede reconocer este elemento (las marcas viejas usaban solo uno). */
const textosAceptados = (el: any): string[] => {
  const t = tagDe(el);
  if (t === 'select' || t === 'textarea' || (t === 'input' && ['submit', 'button', 'reset'].indexOf(attr(el, 'type').toLowerCase()) < 0)) {
    const l = textosDeCampo(el);
    return l.length ? l : [''];
  }
  return [textoDe(el)];
};

const TEXTO_PROPIO = ['th', 'caption', 'p', 'li', 'dt', 'dd', 'h5', 'h6', 'label', 'legend'];

/** Contenedor sin título: su nombre accesible, o (en un encabezado de columna, párrafo...) su propio texto corto. */
const textoDeContenedor = (el: any): string => {
  const t = primerTitulo(el);
  if (t) return t;
  const a = normalizarTexto(attr(el, 'aria-label') || attr(el, 'title'));
  if (a) return a;
  if (TEXTO_PROPIO.indexOf(tagDe(el)) >= 0) return normalizarTexto(el.textContent);
  return '';
};

/**
 * Texto de la huella. Un control usa el suyo; un contenedor, el de su primer título.
 * Es la MISMA función al marcar y al buscar: por eso las dos puntas coinciden.
 * (Se lee textContent, no innerText: innerText fuerza el diseño de la página y, dentro del observador, la haría lenta.)
 */
export const textoDe = (el: any): string => (esMarcable(el) ? textoDeControl(el) : textoDeContenedor(el));

/** Atributos donde las librerías de tooltip guardan el nombre de un botón de solo icono. */
const ATRIBUTOS_TOOLTIP = ['mattooltip', 'ptooltip', 'ngbtooltip', 'data-original-title', 'data-bs-original-title', 'tooltip'];

/** Clase (o texto de un <mat-icon>) de un icono → cómo se llama el botón que lo trae. El orden importa: el primero que coincide gana. */
const ICONOS = [
  ['fa-eye', 'Ver'], ['visibility', 'Ver'],
  ['fa-pen-to-square', 'Editar'], ['fa-pen', 'Editar'], ['fa-pencil', 'Editar'], ['fa-edit', 'Editar'], ['pencil', 'Editar'], ['edit', 'Editar'],
  ['fa-trash', 'Eliminar'], ['fa-trash-alt', 'Eliminar'], ['fa-trash-can', 'Eliminar'], ['delete', 'Eliminar'],
  ['fa-download', 'Descargar'], ['fa-upload', 'Subir'], ['fa-plus', 'Agregar'], ['add', 'Agregar'],
  ['fa-ellipsis-v', 'Más opciones'], ['fa-ellipsis-h', 'Más opciones'], ['ellipsis', 'Más opciones'], ['more_vert', 'Más opciones'], ['more_horiz', 'Más opciones'],
  ['fa-print', 'Imprimir'], ['fa-search', 'Buscar'], ['search', 'Buscar'],
  ['fa-file-excel', 'Exportar a Excel'], ['fa-file-pdf', 'Descargar PDF'],
  ['fa-sync', 'Actualizar'], ['fa-rotate', 'Actualizar'], ['refresh', 'Actualizar'],
  ['fa-filter', 'Filtrar'], ['fa-times', 'Cerrar'], ['fa-xmark', 'Cerrar'], ['close', 'Cerrar'],
  ['fa-check', 'Aprobar'], ['fa-ban', 'Bloquear'], ['fa-lock', 'Bloquear'], ['fa-unlock', 'Desbloquear'], ['fa-key', 'Clave'],
  ['fa-cog', 'Configurar'], ['fa-gear', 'Configurar'], ['fa-copy', 'Copiar'],
  ['fa-paper-plane', 'Enviar'], ['fa-send', 'Enviar'], ['fa-money', 'Pagos'], ['fa-dollar', 'Pagos'],
];

/** Nombre que sugiere la clase de un icono («fa fa-trash» → «Eliminar»), o '' si no se reconoce. Función pura. */
export const nombreDeIcono = (clases: any): string => {
  const fichas = String(clases === null || clases === undefined ? '' : clases).toLowerCase().split(/\s+/).filter((x: any) => !!x);
  for (const ficha of fichas) {
    for (const par of ICONOS) {
      if (ficha === par[0] || ficha.endsWith('-' + par[0])) return par[1];
    }
  }
  return '';
};

/** Primer icono dentro del botón (<i>, <svg>, <mat-icon>), hasta 4 niveles: su nombre por clase o, en <mat-icon>, por su texto. */
const nombreDeIconoInterno = (el: any, nivel: number): string => {
  const hijos = (el && el.children) || [];
  for (let i = 0; i < hijos.length; i++) {
    const h = hijos[i];
    const t = tagDe(h);
    if (t === 'i' || t === 'svg' || t === 'mat-icon' || t === 'span') {
      const n = nombreDeIcono(attr(h, 'class') + (t === 'mat-icon' ? ' ' + normalizarTexto(h.textContent) : ''));
      if (n) return n;
    }
    const hondo = nivel < 4 ? nombreDeIconoInterno(h, nivel + 1) : '';
    if (hondo) return hondo;
  }
  return '';
};

/** ¿Ese texto es solo la palabra de un <mat-icon> («edit», «delete»)? Entonces el botón es de solo icono. */
const esTextoDeMatIcon = (el: any, texto: string, nivel: number): boolean => {
  const hijos = (el && el.children) || [];
  for (let i = 0; i < hijos.length; i++) {
    const h = hijos[i];
    if (tagDe(h) === 'mat-icon' && normalizarTexto(h.textContent) === texto) return true;
    if (nivel < 4 && esTextoDeMatIcon(h, texto, nivel + 1)) return true;
  }
  return false;
};

/**
 * Nombre que se sugiere al tocar un elemento, para que un botón de solo icono no quede sin nombre. En este orden:
 * texto visible, aria-label, title (los tres ya los resuelve textoDe), atributos de tooltip, nombre del campo y,
 * al final, el icono interno. Solo propone: la huella guardada sigue usando textoDe.
 */
/** ¿Botón que abre un menú (⋮)? Por aria-haspopup, por su clase o por el dibujo de tres puntos (svg con 3 círculos). */
const esBotonDeMenu = (el: any, nivel = 0): boolean => {
  if (nivel === 0 && (attr(el, 'aria-haspopup') || /popover|dropdown-toggle|menu-trigger|kebab/i.test(attr(el, 'class')))) return true;
  const hijos = (el && el.children) || [];
  for (let i = 0; i < hijos.length; i++) {
    const h = hijos[i];
    if (tagDe(h) === 'svg') {
      let circulos = 0;
      for (let j = 0; j < ((h.children || []).length); j++) if (tagDe(h.children[j]) === 'circle') circulos++;
      if (circulos === 3) return true;
    }
    if (nivel < 3 && esBotonDeMenu(h, nivel + 1)) return true;
  }
  return false;
};

/** Si el elemento está en una celda de tabla, el título de su columna («Dispositivos»). */
const tituloDeColumna = (el: any): string => {
  let td = el;
  for (let i = 0; td && i < 6 && tagDe(td) !== 'td'; i++) td = td.parentElement;
  const tr = td && tagDe(td) === 'td' ? td.parentElement : null;
  if (!tr) return '';
  const indice = Array.prototype.indexOf.call(tr.children || [], td);
  let tabla = tr;
  for (let i = 0; tabla && i < 4 && tagDe(tabla) !== 'table'; i++) tabla = tabla.parentElement;
  const thead = tabla ? Array.prototype.find.call(tabla.children || [], (h: any) => tagDe(h) === 'thead') : null;
  const filaTitulos = thead ? Array.prototype.find.call(thead.children || [], (h: any) => tagDe(h) === 'tr') : null;
  const th = filaTitulos && indice >= 0 ? (filaTitulos.children || [])[indice] : null;
  return th ? normalizarTexto(th.textContent) : '';
};

export const nombreSugeridoDe = (el: any): string => {
  let propio = textoDe(el);
  if (!esMarcable(el)) return propio;
  // Un número suelto («2 dispositivos» pintado como «2») no sirve de nombre.
  if (/^[\d\s.,%$#+-]+$/.test(propio)) propio = '';
  const soloIcono = !propio || esTextoDeMatIcon(el, propio, 0);
  if (!soloIcono) return propio;
  // Un botón con <mat-icon> trae el nombre del icono como texto: el aria-label y el title valen más.
  const rotulo = normalizarTexto(attr(el, 'aria-label') || attr(el, 'title'));
  if (rotulo) return rotulo;
  for (const a of ATRIBUTOS_TOOLTIP) {
    const v = normalizarTexto(attr(el, a));
    if (v) return v;
  }
  const t = tagDe(el);
  const campo = t === 'button' || t === 'a' ? '' : textosDeCampo(el)[0];
  if (campo) return campo;
  return nombreDeIconoInterno(el, 0) || (esBotonDeMenu(el) ? 'Más opciones' : '') || tituloDeColumna(el);
};

// ───────────────────────── Selector ─────────────────────────

/** Un id que parezca escrito a mano: sin contadores ni prefijos de librerías (mat-input-3, ng-tns-c12, uuid...). */
const idEstable = (id: string): boolean => {
  if (!id || id.length > 40) return false;
  if (!/^[A-Za-z][A-Za-z0-9_-]*$/.test(id)) return false;
  if (/^(mat|cdk|ng|ngb|ngx|mbsc|ui|react|radix|headlessui|popper|tippy|select2|ember|jqui)[-_]/i.test(id)) return false;
  if (/\d{2,}/.test(id) || /\d$/.test(id)) return false;
  return true;
};

const CLASES_DE_ESTADO = [
  'active', 'show', 'open', 'opened', 'hover', 'focus', 'focused', 'selected', 'disabled', 'checked',
  'collapsed', 'collapsing', 'expanded', 'visible', 'hidden', 'invalid', 'valid', 'dirty', 'pristine',
  'touched', 'untouched', 'loading', 'fade', 'in', 'out', 'was-validated',
];

/** Clase que no cambia con el estado del elemento (ni con una variante de tailwind como hover:). */
const claseEstable = (c: string): boolean => {
  if (!c || c.indexOf(':') >= 0) return false;
  if (!/^-?[A-Za-z_]/.test(c)) return false;
  const k = c.toLowerCase();
  if (CLASES_DE_ESTADO.indexOf(k) >= 0) return false;
  if (/^(ng-|cdk-|is-|has-|ui-state-|p-highlight|p-focus|p-disabled|p-hover|mat-focused|mat-mdc-focus)/.test(k)) return false;
  if (/[-_](active|selected|hover|focus|focused|disabled|open|opened|show|shown|checked|expanded|collapsed|loading|invalid|valid|visible|hidden)$/.test(k)) return false;
  if (/\d{4,}/.test(k)) return false; // hashes y contadores
  return true;
};

const escaparClase = (c: string): string => c.replace(/[^A-Za-z0-9_-]/g, (m: string) => '\\' + m);

const esHostApp = (el: any): boolean => /^app-/.test(tagDe(el));

/** Componente de otra librería: se prefiere siempre el app-xxx propio. */
const esOtroComponente = (el: any): boolean => {
  const t = tagDe(el);
  return t.indexOf('-') > 0 && !/^(ng-|ngb-|ngx-|mat-|cdk-|mbsc-|p-)/.test(t) && t !== 'router-outlet';
};

const pasoDe = (el: any, porId: boolean): string => {
  const tag = tagDe(el);
  if (porId) {
    const id = attr(el, 'id');
    if (idEstable(id)) return tag + '#' + id;
  }
  const clases = attr(el, 'class').split(/\s+/).filter(claseEstable).slice(0, MAX_CLASES);
  return tag + clases.map((c: string) => '.' + escaparClase(c)).join('');
};

/**
 * Camino desde el ancestro ancla más cercano hasta el elemento. Ancla = host de un componente propio (app-xxx)
 * o un id estable; si no hay, otro componente con guion; si tampoco, los últimos pasos del camino.
 */
const selectorDe = (el: any): string => {
  const cadena: any[] = []; // del elemento hacia arriba
  let n = el;
  let ancla = -1;
  let anclaPorId = false;
  while (n && cadena.length < 40) {
    const t = tagDe(n);
    if (t === 'body' || t === 'html') break;
    cadena.push(n);
    if (esHostApp(n)) { ancla = cadena.length - 1; break; }
    if (idEstable(attr(n, 'id'))) { ancla = cadena.length - 1; anclaPorId = true; break; }
    n = n.parentElement;
  }
  if (!cadena.length) return tagDe(el) || '*';
  if (ancla < 0) {
    for (let i = 0; i < cadena.length; i++) {
      if (esOtroComponente(cadena[i])) { ancla = i; break; }
    }
  }
  if (ancla < 0) ancla = Math.min(cadena.length, MAX_PASOS_SIN_ANCLA) - 1;
  const pasos: string[] = [];
  for (let i = ancla; i >= 0; i--) pasos.push(pasoDe(cadena[i], i === ancla && anclaPorId));
  return pasos.join(' > ');
};

// ───────────────────────── Huella ─────────────────────────

const etiquetaDe = (el: any, texto: string): string => {
  if (!esMarcable(el)) return texto ? 'Sección «' + texto + '»' : 'Sección sin título';
  const t = tagDe(el);
  const tipoInput = attr(el, 'type').toLowerCase();
  const esBoton = t === 'button' || t === 'a' || attr(el, 'role').toLowerCase() === 'button'
    || (t === 'input' && (tipoInput === 'submit' || tipoInput === 'button' || tipoInput === 'reset'));
  const clase = esBoton ? 'Botón' : 'Campo';
  return texto ? clase + ' «' + texto + '»' : clase + ' sin texto';
};

/** { selector, texto, etiqueta } del elemento. Es lo que se guarda en la marca y se vuelve a buscar luego. */
export const huellaDe = (el: any): HuellaRbac => {
  const texto = textoDe(el);
  return { selector: selectorDe(el), texto, etiqueta: etiquetaDe(el, texto) };
};

/** Lo que la pestaña propone como nombre al tocar el elemento. */
export const sugerenciaDe = (el: any): SugerenciaRbac => ({ tipo: tipoDe(el), nombre: nombreSugeridoDe(el) });

/**
 * Elementos de `raiz` que responden a la huella: mismo selector y, si la huella trae texto, el mismo texto (para un
 * campo vale cualquiera de sus nombres: etiqueta, placeholder, aria-label...). Si trae `indice` («solo este»), solo
 * el que ocupa esa posición entre los que coinciden, en orden del documento.
 */
export const buscarPorHuella = (raiz: any, huella: any): any[] => {
  if (!raiz || !huella || !huella.selector) return [];
  let lista: any[] = [];
  try {
    lista = Array.prototype.slice.call(raiz.querySelectorAll(huella.selector));
  } catch (e) {
    return []; // selector inválido: no se encuentra nada, no se rompe la pantalla
  }
  const texto = normalizarTexto(huella.texto);
  if (texto) lista = lista.filter((el: any) => textosAceptados(el).indexOf(texto) >= 0);
  if (typeof huella.indice === 'number' && huella.indice >= 0) return lista[huella.indice] ? [lista[huella.indice]] : [];
  return lista;
};

/** Huella que apunta SOLO a `el`: la suya más su posición entre los que coinciden con ella (`indice`). */
export const huellaSoloEste = (raiz: any, el: any): HuellaRbac => {
  const h = huellaDe(el);
  const lista = buscarPorHuella(raiz, h);
  const i = lista.indexOf(el);
  return i >= 0 ? { ...h, indice: i } : h;
};

// ───────────────────────── Lo que el código ya protege ─────────────────────────

/** Códigos de permiso del atributo `data-rbac-permiso` de un elemento (vacío si no lo trae). */
export const permisosDeElemento = (el: any): string[] => {
  const v = attr(el, ATRIBUTO_PERMISO);
  return v ? v.split(/[\s,]+/).filter((x: string) => x !== '') : [];
};

/** Elemento protegido más cercano (él o un ancestro) y sus códigos: { el, permisos } o null. */
export const protegidoDe = (el: any): any => {
  let n = el;
  for (let i = 0; n && i < 80; i++) {
    const p = permisosDeElemento(n);
    if (p.length) return { el: n, permisos: p };
    n = n.parentElement;
  }
  return null;
};

/** Todos los elementos de `raiz` con `data-rbac-permiso`: [{ el, permisos }]. */
export const elementosProtegidos = (raiz: any): any[] => {
  if (!raiz || !raiz.querySelectorAll) return [];
  const lista = raiz.querySelectorAll('[' + ATRIBUTO_PERMISO + ']');
  const salida: any[] = [];
  for (let i = 0; i < lista.length; i++) {
    const p = permisosDeElemento(lista[i]);
    if (p.length) salida.push({ el: lista[i], permisos: p });
  }
  return salida;
};

// ───────────────────────── Rutas ─────────────────────────

const segmentosDe = (r: any): string[] => String(r === null || r === undefined ? '' : r)
  .replace(/^[A-Za-z][A-Za-z0-9+.-]*:\/\/[^/]*/, '')
  .split(/[?#]/)[0]
  .split('/')
  .filter((s: string) => s !== '');

/** ¿La URL cae en el patrón? `:param` vale un segmento cualquiera; se ignoran query, hash y la barra final. */
export const rutaCoincide = (patron: any, url: any): boolean => {
  if (!patron || url === null || url === undefined) return false;
  const p = segmentosDe(patron);
  const u = segmentosDe(url);
  if (p.length !== u.length) return false;
  for (let i = 0; i < p.length; i++) {
    if (p[i].length > 1 && p[i].charAt(0) === ':') continue;
    if (p[i] !== u[i]) return false;
  }
  return true;
};

/**
 * ¿Son el mismo patrón de ruta? Tramo por tramo: uno literal solo iguala a otro literal igual, y un `:param` solo a
 * otro `:param` (el nombre no importa). Así `/console/merchants/:code` NO es `/console/merchants/nuevo`.
 */
export const mismoPatron = (a: any, b: any): boolean => {
  if (!a || !b) return false;
  const x = segmentosDe(a);
  const y = segmentosDe(b);
  if (x.length !== y.length) return false;
  for (let i = 0; i < x.length; i++) {
    const px = x[i].length > 1 && x[i].charAt(0) === ':';
    const py = y[i].length > 1 && y[i].charAt(0) === ':';
    if (px !== py) return false;
    if (!px && x[i] !== y[i]) return false;
  }
  return true;
};

/**
 * El patrón de la ruta activa, con el mismo formato que guarda «Agregar permisos» (`/console/merchants/:code`): los
 * `routeConfig.path` no vacíos desde la raíz del snapshot del router hasta la hoja primaria, unidos con '/'.
 * Devuelve '' si no se puede armar (entonces el vigilante cae a comparar contra la URL).
 */
export const patronDeRuta = (raiz: any): string => {
  const tramos: string[] = [];
  let nodo = raiz;
  let vueltas = 0;
  while (nodo && vueltas < 50) {
    const path = nodo.routeConfig ? nodo.routeConfig.path : null;
    if (typeof path === 'string' && path !== '') tramos.push(path);
    const hijas = nodo.children || [];
    let siguiente = null;
    for (let i = 0; i < hijas.length; i++) {
      if (hijas[i] && (!hijas[i].outlet || hijas[i].outlet === 'primary')) { siguiente = hijas[i]; break; }
    }
    nodo = siguiente;
    vueltas++;
  }
  return tramos.length ? '/' + tramos.join('/') : '';
};

// ───────────────────────── Lo que usa el vigilante ─────────────────────────

/**
 * Marcas de la ruta actual que el usuario NO puede ver. `tienePermiso` es AuthService.hasPermission: un usuario
 * sin RBAC devuelve true siempre y por eso no se esconde nada. Una marca mal formada se ignora.
 * `patron` es el patrón de la ruta activa (ver `patronDeRuta`): si viene, la marca se compara contra él y un `:param`
 * ya no se come a una ruta literal hermana (`/merchants/:code` vs `/merchants/nuevo`). Sin patrón se compara contra la URL.
 */
export const marcasSinPermiso = (marcas: any, url: string, tienePermiso: any, patron: any = ''): any[] => {
  const salida: any[] = [];
  if (!marcas || !marcas.length) return salida;
  for (let i = 0; i < marcas.length; i++) {
    const m = marcas[i];
    if (!m || !m.ruta || !m.permiso) continue;
    if (!(patron ? mismoPatron(m.ruta, patron) : rutaCoincide(m.ruta, url))) continue;
    let permitido = true;
    try {
      permitido = !!tienePermiso(m.permiso);
    } catch (e) {
      permitido = true; // ante la duda, no se esconde
    }
    if (!permitido) salida.push(m);
  }
  return salida;
};

/** Pone `data-rbac-oculto` a todo lo que responda a una marca de acción o sección. Devuelve cuántos tocó. */
/**
 * Esconde un elemento. Algunas pantallas fuerzan `display: block !important` y le ganan a la regla de styles.css: el
 * estilo en línea con !important gana siempre. Se guarda el que tenía para devolverlo al quitarlo.
 */
const esconder = (el: any) => {
  el.setAttribute(ATRIBUTO_OCULTO, '');
  if (el.style && el.style.setProperty) {
    el.setAttribute(ATRIBUTO_DISPLAY_PREVIO, el.style.getPropertyValue('display') + '|' + el.style.getPropertyPriority('display'));
    el.style.setProperty('display', 'none', 'important');
  }
};

export const ocultarMarcas = (raiz: any, marcas: any[]): number => {
  let tocados = 0;
  for (let i = 0; i < marcas.length; i++) {
    const m = marcas[i];
    if (!m || m.tipo === 'pantalla' || !m.huella) continue;
    const encontrados = buscarPorHuella(raiz, m.huella);
    for (let j = 0; j < encontrados.length; j++) {
      const el = encontrados[j];
      if (!el.hasAttribute(ATRIBUTO_OCULTO)) {
        esconder(el);
        tocados++;
      }
    }
  }
  return tocados;
};

/** Marcas de tipo pantalla (de CUALQUIER ruta) cuyo permiso el usuario no tiene: pantallas que no puede abrir. */
export const pantallasSinPermiso = (marcas: any, tienePermiso: any): any[] => {
  const salida: any[] = [];
  for (const m of marcas || []) {
    if (!m || m.tipo !== 'pantalla' || !m.ruta || !m.permiso) continue;
    let permitido = true;
    try { permitido = !!tienePermiso(m.permiso); } catch (e) { permitido = true; }
    if (!permitido) salida.push(m);
  }
  return salida;
};

/** La ruta a la que lleva un enlace, sin dominio, parámetros ni `#` (vacía si no lleva a una ruta). */
export const rutaDeEnlace = (el: any): string => {
  const href = attr(el, 'href');
  if (!href || /^(javascript:|mailto:|tel:|#)/i.test(href)) return '';
  return href.replace(/^https?:\/\/[^/]+/i, '').split(/[?#]/)[0].replace(/\/+$/, '') || '/';
};

/**
 * Esconde los enlaces (<a href>) que llevan a una pantalla que el usuario no puede abrir: la tarjeta de Parámetros
 * del Sistema o la opción del menú. Sin esto la pantalla protegida lo saca al entrar, pero su acceso seguía a la vista.
 */
export const ocultarEnlacesA = (raiz: any, vedadas: any[]): number => {
  if (!raiz || !raiz.querySelectorAll || !vedadas || !vedadas.length) return 0;
  const enlaces = raiz.querySelectorAll('a[href]');
  let tocados = 0;
  for (let i = 0; i < enlaces.length; i++) {
    const el = enlaces[i];
    if (el.hasAttribute(ATRIBUTO_OCULTO)) continue;
    const ruta = rutaDeEnlace(el);
    if (ruta && vedadas.some((m: any) => rutaCoincide(m.ruta, ruta))) {
      esconder(el);
      tocados++;
    }
  }
  return tocados;
};

/** Quita `data-rbac-oculto` de todo lo que lo tenga. Devuelve cuántos devolvió a la vista. */
export const quitarOcultos = (raiz: any): number => {
  if (!raiz || !raiz.querySelectorAll) return 0;
  const lista = raiz.querySelectorAll('[' + ATRIBUTO_OCULTO + ']');
  let quitados = 0;
  for (let i = 0; i < lista.length; i++) {
    const el = lista[i];
    el.removeAttribute(ATRIBUTO_OCULTO);
    if (el.style && el.style.setProperty && el.hasAttribute(ATRIBUTO_DISPLAY_PREVIO)) {
      const [valor, prioridad] = String(el.getAttribute(ATRIBUTO_DISPLAY_PREVIO)).split('|');
      if (valor) el.style.setProperty('display', valor, prioridad || '');
      else el.style.removeProperty('display');
      el.removeAttribute(ATRIBUTO_DISPLAY_PREVIO);
    }
    quitados++;
  }
  return quitados;
};

/** Modo del iframe marcador: «marcar» (cada clic selecciona) o «usar» (los clics pasan a la pantalla; el interceptor sigue bloqueando todo lo que no sea GET). */
export const MODO_MARCADOR_POR_OMISION = 'marcar';

/** Cualquier valor raro vale «marcar»: ante la duda, la pantalla no se usa. */
export const normalizarModo = (modo: any) => (modo === 'usar' ? 'usar' : MODO_MARCADOR_POR_OMISION);

/** Con Alt presionado el modo es «usar» de forma temporal. */
export const modoEfectivo = (modo: any, alt: any) => (alt === true || normalizarModo(modo) === 'usar' ? 'usar' : 'marcar');

/**
 * Qué hace el manejador de eventos del iframe: 'marcar' (frena y selecciona), 'frenar' (frena sin seleccionar) o 'pasar'.
 * En «usar» todo pasa menos el envío de formularios (evitaría recargar el iframe; nada se guarda de todos modos).
 */
export const accionDeEvento = (modo: any, tipo: any) => {
  if (modoEfectivo(modo, false) === 'usar') return tipo === 'submit' ? 'frenar' : 'pasar';
  return tipo === 'click' ? 'marcar' : 'frenar';
};
